import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AngelIcon from '@/components/base/AngelIcon';
import RgChapterGuide from '@/pages/ai-mentor/components/RgChapterGuide';
import { buildRgGuidance } from '@/features/ai-angel/rgKnowledge.mjs';

type CompanionMode = 'greeting' | 'chapterGuide' | 'lookFind' | 'connectDots' | 'treasureMap' | 'treasureArchive' | 'gratitude' | 'memory' | 'livingWitness';
type TreasureStage = 'observe' | 'reflect' | 'practice' | 'reveal';
type MentorCheckpointKey = 'evidence' | 'gratitude' | 'greaterGood' | 'diamondAnchor' | 'oneAxis' | 'dailyReset';

const treasureStageLabel: Record<TreasureStage, string> = {
  observe: '觀察',
  reflect: '反思',
  practice: '練習',
  reveal: '揭示',
};

const mentorCheckpointLabel: Record<MentorCheckpointKey, string> = {
  evidence: '回歸中軸有行而果，留下可查核 Evidence',
  gratitude: '看見助力與來時路，感恩源頭救恩線，可進入 >5.5D 大生命角色',
  greaterGood: '行動由小私擴展到他人、團隊，逐步進入 >5.5D 大生命',
  diamondAnchor: '身心意行較一致，5D 鑽石心錨能在壓力中保持明亮',
  oneAxis: '持續守在中軸、自我實現，可錨定一之軸理並回到衷愛',
  dailyReset: '五秒歸零：早晨如新生命第一天，夜晚如生命最後一天，完成每日交帳',
};

const treasureStageRelation: Record<TreasureStage, string> = {
  observe: '承接 Look & Find：看清事件、感受、身體反應與自動念頭。',
  reflect: '承接 Connect Dots：辨識反覆模式、RG章節、主軸與最低缺口。',
  practice: '把看見轉成一項中軸改變，設定完成時間並留下 Evidence。',
  reveal: '回看實踐後的變化，提煉可被記住與分享的生命寶藏。',
};

const treasureStagePrompt: Record<TreasureStage, { text: string; textEn: string }> = {
  observe: {
    text: '先不分析。回看剛才的事件：實際發生了什麼？你的身體、感受與自動念頭各是什麼？',
    textEn: 'Without analyzing yet, what happened, and what did your body, feelings, and automatic thoughts show?',
  },
  reflect: {
    text: '現在進入反思：哪個反覆模式正在運轉？在RG三章路徑中，哪一條是本次主軸？六力或BMSA的最低缺口在哪裡？',
    textEn: 'Now reflect: what pattern is repeating, what is the main axis, and where is the lowest gap?',
  },
  practice: {
    text: '現在進入練習：如果只做一項中軸改變，你要做什麼？何時完成？準備留下什麼 Evidence？',
    textEn: 'Now practice: what one centered change will you make, by when, and what Evidence will you leave?',
  },
  reveal: {
    text: '現在進入揭示：回看你的觀察、反思與練習，這次歷程真正讓你看見了什麼？它帶來什麼新的選擇或生命寶藏？',
    textEn: 'Now reveal: looking back at observation, reflection, and practice, what has this journey shown you?',
  },
};

interface ConversationEntry {
  from: 'angel' | 'user';
  text: string;
  textEn?: string;
}

interface TreasureJourneyArchive {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  lookFindNotes: string[];
  connectedChapters: string[];
  connectDotsResult: string;
  recommendedTreasure: TreasureStage;
  selectedTreasure: TreasureStage | null;
  treasureNotes: Partial<Record<TreasureStage, string>>;
  mentorCheckpoints: Partial<Record<MentorCheckpointKey, boolean>>;
  conversations: Partial<Record<CompanionMode, ConversationEntry[]>>;
  status: 'in_progress' | 'completed';
}

const TREASURE_ARCHIVE_KEY = 'ai-angel-treasure-archives-v1';

export default function AIMentor() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<CompanionMode>('greeting');
  const [conversation, setConversation] = useState<ConversationEntry[]>([]);
  const [userInput, setUserInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [lookFindNotes, setLookFindNotes] = useState<string[]>([]);
  const [connectedChapters, setConnectedChapters] = useState<string[]>([]);
  const [connectDotsResult, setConnectDotsResult] = useState('');
  const [recommendedTreasure, setRecommendedTreasure] = useState<TreasureStage>('observe');
  const [selectedTreasure, setSelectedTreasure] = useState<TreasureStage | null>(null);
  const [treasureNotes, setTreasureNotes] = useState<Partial<Record<TreasureStage, string>>>({});
  const [mentorCheckpoints, setMentorCheckpoints] = useState<Partial<Record<MentorCheckpointKey, boolean>>>({});
  const [conversationByMode, setConversationByMode] = useState<Partial<Record<CompanionMode, ConversationEntry[]>>>({});
  const [treasureArchives, setTreasureArchives] = useState<TreasureJourneyArchive[]>([]);
  const [activeTreasureId, setActiveTreasureId] = useState<string | null>(null);
  const [archiveLoaded, setArchiveLoaded] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Witness state
  const [witnessVisibility, setWitnessVisibility] = useState<'anonymous' | 'public' | 'mentorOnly' | null>(null);
  const [witnessSent, setWitnessSent] = useState(false);

  const setModeCallback = useCallback((m: CompanionMode) => {
    setConversationByMode((histories) => ({ ...histories, [mode]: conversation }));
    setMode(m);
    setConversation(conversationByMode[m] || []);
    setUserInput(m === 'connectDots' && lookFindNotes.length > 0 && !(conversationByMode[m]?.length)
      ? lookFindNotes.join('；')
      : '');
    setSubmitted(false);
    setWitnessVisibility(null);
    setWitnessSent(false);
  }, [conversation, conversationByMode, lookFindNotes, mode]);

  useEffect(() => {
    setConversationByMode((histories) => ({ ...histories, [mode]: conversation }));
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, mode]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(TREASURE_ARCHIVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setTreasureArchives(parsed);
      }
    } catch {
      // Keep the journey usable even when browser storage is unavailable.
    } finally {
      setArchiveLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!archiveLoaded) return;
    try {
      window.localStorage.setItem(TREASURE_ARCHIVE_KEY, JSON.stringify(treasureArchives));
    } catch {
      // The current journey remains on screen when browser storage is unavailable.
    }
  }, [archiveLoaded, treasureArchives]);

  const saveTreasureJourney = useCallback(() => {
    const hasJourney = lookFindNotes.length > 0
      || connectedChapters.length > 0
      || Object.values(treasureNotes).some(Boolean);
    if (!hasJourney) {
      setSaveNotice('請先完成一段 Look & Find、Connect Dots 或尋寶圖內容。');
      return;
    }

    const now = new Date().toISOString();
    const id = activeTreasureId || `TM-${Date.now()}`;
    const previous = treasureArchives.find((item) => item.id === id);
    const stageOrder: TreasureStage[] = ['observe', 'reflect', 'practice', 'reveal'];
    const archive: TreasureJourneyArchive = {
      id,
      title: lookFindNotes[0]?.slice(0, 32) || connectedChapters.join(' → ') || '人生遊戲尋寶圖',
      createdAt: previous?.createdAt || now,
      updatedAt: now,
      lookFindNotes,
      connectedChapters,
      connectDotsResult,
      recommendedTreasure,
      selectedTreasure,
      treasureNotes,
      mentorCheckpoints,
      conversations: { ...conversationByMode, [mode]: conversation },
      status: stageOrder.every((stage) => Boolean(treasureNotes[stage])) ? 'completed' : 'in_progress',
    };

    setTreasureArchives((items) => [archive, ...items.filter((item) => item.id !== id)]);
    setActiveTreasureId(id);
    setSaveNotice('尋寶圖已儲存；下次可從「我的尋寶圖」繼續。');
  }, [
    activeTreasureId,
    connectedChapters,
    connectDotsResult,
    conversation,
    conversationByMode,
    lookFindNotes,
    mode,
    recommendedTreasure,
    selectedTreasure,
    treasureArchives,
    treasureNotes,
    mentorCheckpoints,
  ]);

  const restoreTreasureJourney = useCallback((archive: TreasureJourneyArchive) => {
    const order: TreasureStage[] = ['observe', 'reflect', 'practice', 'reveal'];
    const nextStage = order.find((stage) => !archive.treasureNotes?.[stage]) || 'reveal';
    const histories = archive.conversations || {};
    setActiveTreasureId(archive.id);
    setLookFindNotes(archive.lookFindNotes || []);
    setConnectedChapters(archive.connectedChapters || []);
    setConnectDotsResult(archive.connectDotsResult || '');
    setRecommendedTreasure(archive.recommendedTreasure || 'observe');
    setSelectedTreasure(nextStage);
    setTreasureNotes(archive.treasureNotes || {});
    setMentorCheckpoints(archive.mentorCheckpoints || {});
    setConversationByMode(histories);
    setConversation(histories.treasureMap || [{
      from: 'angel',
      text: `歡迎回來。我們從「${treasureStageLabel[nextStage]}」繼續。\n\n${treasureStagePrompt[nextStage].text}`,
      textEn: treasureStagePrompt[nextStage].textEn,
    }]);
    setUserInput(archive.treasureNotes?.[nextStage] || '');
    setSubmitted(false);
    setSaveNotice('已叫回這張尋寶圖，可以從尚未完成的階段繼續。');
    setMode('treasureMap');
  }, []);

  const startNewTreasureJourney = useCallback(() => {
    setActiveTreasureId(null);
    setLookFindNotes([]);
    setConnectedChapters([]);
    setConnectDotsResult('');
    setRecommendedTreasure('observe');
    setSelectedTreasure(null);
    setTreasureNotes({});
    setMentorCheckpoints({});
    setConversationByMode({});
    setConversation([]);
    setUserInput('');
    setSubmitted(false);
    setSaveNotice('');
    setMode('lookFind');
  }, []);

  // ── Look & Find: conversational ──────────────────
  const startLookFind = useCallback(() => {
    setConversation([
      {
        from: 'angel',
        text: '今天生命一直重複出現什麼？',
        textEn: 'What has Life been repeatedly showing you today?',
      },
    ]);
  }, []);

  const handleLookFindReply = useCallback(() => {
    if (!userInput.trim()) return;
    const userTurns = conversation.filter(({ from }) => from === 'user').length;
    const followUps = [
      {
        text: '這件事讓你最有感覺的是什麼？',
        textEn: 'What about this touched you the most?',
      },
      {
        text: '當它發生時，你的身體先有什麼感覺？腦中又立刻出現什麼念頭？',
        textEn: 'What did your body feel first, and what thought appeared automatically?',
      },
      {
        text: '在你剛才看見的內容裡，哪些是已發生的事實，哪些是你的解讀？',
        textEn: 'What are the facts, and what is your interpretation?',
      },
      {
        text: '如果今天只做一項中軸的改變，你願意先改變哪一小點？',
        textEn: 'What is one centered change you are willing to make today?',
      },
      {
        text: '你準備何時完成？完成後，要留下什麼可以回看的 Evidence？',
        textEn: 'When will you complete it, and what Evidence will you leave?',
      },
      {
        text: '我收到了。完成這一步後，你觀察到自己、關係或事情出現什麼不同？你也可以繼續說，我會陪你看下一小步。',
        textEn: 'After this step, what changed in you, the relationship, or the situation?',
      },
    ];
    const next = followUps[Math.min(userTurns, followUps.length - 1)];
    const answer = userInput.trim();
    setConversation([
      ...conversation,
      { from: 'user', text: answer },
      { from: 'angel', ...next },
    ]);
    setLookFindNotes((notes) => [...notes, answer]);
    setRecommendedTreasure('reflect');
    setUserInput('');
    setSubmitted(false);
  }, [userInput, conversation]);

  // ── Connect the Living Dots: conversational ──────
  const importTreasureTimeline = useCallback(() => {
    if (treasureArchives.length === 0) {
      setUserInput(lookFindNotes.join('；'));
      return;
    }
    const summaries = treasureArchives.map((archive) => {
      const treasure = archive.treasureNotes?.reveal
        || archive.treasureNotes?.practice
        || archive.lookFindNotes?.[0]
        || archive.title;
      return `${new Date(archive.updatedAt).toLocaleDateString('zh-TW')}｜${archive.connectedChapters?.join('→') || '待辨識'}｜${treasure}`;
    });
    const compact = summaries.join('\n').slice(0, 1800);
    setUserInput(`請連結我這一段時間累積的 Treasure（不受一週限制）：\n${compact}`);
  }, [lookFindNotes, treasureArchives]);

  const handleConnectDotsReply = useCallback(() => {
    if (!userInput.trim()) return;
    const guidance = buildRgGuidance(userInput.trim(), 'mentor');
    const newConv: ConversationEntry[] = [
      ...conversation,
      { from: 'user', text: userInput.trim() },
      {
        from: 'angel',
        text: guidance.reply,
        textEn: `${guidance.chapters.map(({ id }) => id).join(' + ')} · ${guidance.loadedChars} chars · ${guidance.savedPercent}% saved versus Final v0.8`,
      },
    ];
    setConversation(newConv);
    setConnectedChapters(guidance.chapters.map(({ id }) => id));
    setConnectDotsResult(guidance.reply);
    setRecommendedTreasure(guidance.needsClarification ? 'reflect' : 'practice');
    setSelectedTreasure(null);
    setUserInput('');
    setSubmitted(false);
  }, [userInput, conversation]);

  // ── Treasure Map: level selection ─────────────────
  const handleTreasureLevel = useCallback((stage: TreasureStage, level: string, levelEn: string) => {
    setSelectedTreasure(stage);
    setConversation([
      {
        from: 'angel',
        text: `今天，生命比較像是在邀請你「${level}」。\n\n${treasureStagePrompt[stage].text}`,
        textEn: `Today, Life seems to be inviting you to "${levelEn}".\n\n${treasureStagePrompt[stage].textEn}`,
      },
    ]);
    setUserInput(treasureNotes[stage] || '');
    setSubmitted(false);
  }, [treasureNotes]);

  const handleTreasureMapReply = useCallback(() => {
    if (!userInput.trim() || !selectedTreasure) return;
    const answer = userInput.trim();
    const updatedNotes = { ...treasureNotes, [selectedTreasure]: answer };
    const order: TreasureStage[] = ['observe', 'reflect', 'practice', 'reveal'];
    const currentIndex = order.indexOf(selectedTreasure);
    const nextStage = order[currentIndex + 1];
    const revealText = selectedTreasure === 'reveal'
      ? [
          '這次揭示已形成：',
          updatedNotes.observe ? `觀察｜${updatedNotes.observe}` : '',
          updatedNotes.reflect ? `反思｜${updatedNotes.reflect}` : '',
          updatedNotes.practice ? `練習｜${updatedNotes.practice}` : '',
          `揭示｜${answer}`,
          '這不是替你下結論，而是把你親自走過的四層歷程提領成可回看的生命寶藏。',
        ].filter(Boolean).join('\n')
      : `我已記下你的「${treasureStageLabel[selectedTreasure]}」。${nextStage ? `下一步可以進入「${treasureStageLabel[nextStage]}」；也可以留在這一層補充。` : ''}`;
    setTreasureNotes(updatedNotes);
    setConversation([
      ...conversation,
      { from: 'user', text: answer },
      { from: 'angel', text: revealText },
      ...(nextStage ? [{
        from: 'angel' as const,
        text: `接著，我們進入「${treasureStageLabel[nextStage]}」。\n\n${treasureStagePrompt[nextStage].text}`,
        textEn: treasureStagePrompt[nextStage].textEn,
      }] : []),
    ]);
    if (nextStage) setSelectedTreasure(nextStage);
    setUserInput(nextStage ? (updatedNotes[nextStage] || '') : '');
    setSubmitted(false);
  }, [userInput, selectedTreasure, treasureNotes, conversation]);

  // ── Living Witness: share treasure ────────────────
  const handleShareWitness = useCallback(() => {
    if (!witnessVisibility) return;
    setWitnessSent(true);
  }, [witnessVisibility]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 md:px-6 pt-20 md:pt-24 pb-16">
        {/* ── Greeting Header ─────────────────────────── */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <AngelIcon variant="gold" size="lg" />
            <div className="text-left">
              <p className="text-xs text-foreground-400 tracking-wider uppercase">
                AI Angel
              </p>
              <p className="text-sm text-foreground-500">
                {t('aiAngel.companionNote')}
              </p>
            </div>
          </div>

          <h1
            className="text-2xl md:text-3xl text-foreground-800 mb-2"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {t('aiAngel.greeting')}
            <span className="block text-sm text-foreground-400 font-normal mt-1 italic">
              {t('aiAngel.greetingEn')}
            </span>
          </h1>
          <p className="text-sm text-foreground-500 italic">
            {t('aiAngel.journeyContinues')}
          </p>
        </div>

        {/* ── Current Snapshot ─────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10 max-w-2xl mx-auto">
          {[
            { key: 'currentRoot', icon: 'ri-seedling-line', color: 'text-primary-500', val: '自我實現', valEn: 'Self Realization' },
            { key: 'currentNode', icon: 'ri-map-pin-line', color: 'text-accent-500', val: '承擔', valEn: 'Accountability' },
            { key: 'todayTreasure', icon: 'ri-gift-line', color: 'text-amber-400', val: '誠信', valEn: 'Integrity' },
            { key: 'nextHint', icon: 'ri-compass-3-line', color: 'text-secondary-500', val: '觀察', valEn: 'Observe' },
          ].map((item) => (
            <div
              key={item.key}
              className="bg-background-100 border border-foreground-200/40 rounded-xl p-3 text-center"
            >
              <i className={`${item.icon} ${item.color} text-lg mb-1 inline-block`} />
              <p className="text-[10px] text-foreground-400 uppercase tracking-wider mb-0.5">
                {t(`aiAngel.${item.key}`)}
              </p>
              <p className="text-sm font-medium text-foreground-700">{item.val}</p>
              <p className="text-[10px] text-foreground-400">{item.valEn}</p>
            </div>
          ))}
        </div>

        {/* ── Mode Selector ─────────────────────────── */}
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-10">
          {[
            { key: 'greeting' as CompanionMode, icon: 'ri-home-4-line', label: '首頁', labelEn: 'Home' },
            { key: 'chapterGuide' as CompanionMode, icon: 'ri-book-open-line', label: 'RG章節輔導', labelEn: '' },
            { key: 'lookFind' as CompanionMode, icon: 'ri-eye-line', label: 'Look & Find', labelEn: '' },
            { key: 'connectDots' as CompanionMode, icon: 'ri-links-line', label: 'Connect Dots', labelEn: '' },
            { key: 'treasureMap' as CompanionMode, icon: 'ri-map-line', label: 'Treasure Map', labelEn: '' },
            { key: 'treasureArchive' as CompanionMode, icon: 'ri-archive-line', label: '我的尋寶圖', labelEn: '' },
            { key: 'livingWitness' as CompanionMode, icon: 'ri-eye-2-line', label: 'Living Witness', labelEn: '' },
            { key: 'memory' as CompanionMode, icon: 'ri-history-line', label: 'Angel Memory', labelEn: '' },
            { key: 'gratitude' as CompanionMode, icon: 'ri-heart-3-line', label: '感恩', labelEn: 'Gratitude' },
          ].map((m) => (
            <button
              key={m.key}
              onClick={() => setModeCallback(m.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs border transition-all cursor-pointer whitespace-nowrap ${
                mode === m.key
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-foreground-200 text-foreground-500 hover:border-foreground-300 hover:text-foreground-700'
              }`}
            >
              <i className={`${m.icon} text-sm`} />
              <span>{m.label}</span>
            </button>
          ))}
        </div>

        {/* ── Mode Content ─────────────────────────── */}
        <div className="max-w-2xl mx-auto">
          {mode === 'chapterGuide' && <RgChapterGuide />}

          {/* Greeting */}
          {mode === 'greeting' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 text-center">
              <AngelIcon variant="gold" size="md" />
              <p className="text-sm text-foreground-700 mt-4 leading-relaxed">
                {t('aiAngel.companionNote')}
              </p>
              <p className="text-xs text-foreground-400 mt-1">
                {t('aiAngel.companionNoteEn')}
              </p>
              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2">
                <Link
                  to="/journey"
                  className="whitespace-nowrap px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  {t('common.cta.viewJourney')}
                </Link>
                <button
                  onClick={() => { setModeCallback('lookFind'); startLookFind(); }}
                  className="whitespace-nowrap px-5 py-2 rounded-md border border-foreground-200 text-foreground-600 text-sm hover:bg-foreground-50 transition-colors cursor-pointer"
                >
                  {t('aiAngel.modes.lookFind.name')}
                </button>
              </div>
            </div>
          )}

          {/* Look & Find — conversational */}
          {mode === 'lookFind' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-eye-line text-primary-500 text-lg" />
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('aiAngel.modes.lookFind.name')}
                </h3>
              </div>
              <p className="text-xs text-foreground-400 mb-4">
                {t('aiAngel.modes.lookFind.desc')} — {t('aiAngel.modes.lookFind.action')}
              </p>

              {/* Conversation */}
              <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                {conversation.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-xs text-foreground-400">點擊下方按鈕開始對話</p>
                  </div>
                ) : (
                  conversation.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex ${entry.from === 'angel' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-3 ${
                          entry.from === 'angel'
                            ? 'bg-background-50 border border-foreground-200/30'
                            : 'bg-primary-500 text-background-50'
                        }`}
                      >
                        {entry.from === 'angel' && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <AngelIcon variant="gold" size="sm" />
                            <span className="text-[10px] text-foreground-400">AI Angel</span>
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-line">{entry.text}</p>
                        {entry.textEn && (
                          <p className="text-[10px] opacity-60 mt-1">{entry.textEn}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              {!submitted ? (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') conversation.length === 0 ? startLookFind() : handleLookFindReply(); }}
                      className="flex-1 px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-primary-300"
                      placeholder="寫下你的回應……"
                    />
                    <button
                      onClick={conversation.length === 0 ? startLookFind : handleLookFindReply}
                      disabled={conversation.length > 0 && !userInput.trim()}
                      className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {conversation.length === 0 ? '開始' : '回應'}
                    </button>
                  </div>
                  {conversation.some(({ from }) => from === 'user') && (
                    <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-3">
                      <button
                        onClick={() => setSubmitted(true)}
                        className="text-xs text-foreground-400 hover:text-primary-600 transition-colors cursor-pointer"
                      >
                        先記到這裡，暫告一段落
                      </button>
                      <button
                        onClick={() => setModeCallback('connectDots')}
                        className="text-xs text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                      >
                        帶著這些看見，進入 Connect Dots →
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-xs text-foreground-400 italic mb-3">
                    這段看見已經記在對話裡。你可以繼續往下看，也可以重新開始另一件事。
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-4">
                    <button
                      onClick={() => setSubmitted(false)}
                      className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      繼續對話
                    </button>
                    <button
                      onClick={() => setModeCallback('connectDots')}
                      className="text-xs text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                    >
                      進入 Connect Dots
                    </button>
                    <button
                      onClick={() => { setConversation([]); setLookFindNotes([]); setConnectedChapters([]); setRecommendedTreasure('observe'); setSubmitted(false); setUserInput(''); }}
                      className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                    >
                      重新開始
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Connect the Living Dots — conversational */}
          {mode === 'connectDots' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-links-line text-accent-500 text-lg" />
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('aiAngel.modes.connectDots.name')}
                </h3>
              </div>
              <p className="text-xs text-foreground-400 mb-2">
                {t('aiAngel.modes.connectDots.desc')} — {t('aiAngel.modes.connectDots.action')}
              </p>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                <p className="text-[10px] text-foreground-400">
                  可連結任意期間已保存的 Treasure；AI辨識三章路徑，但一次只陪你走最相關的一章。
                </p>
                <button
                  onClick={importTreasureTimeline}
                  className="text-xs text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                >
                  匯入我的尋寶圖
                </button>
              </div>

              {/* Conversation */}
              <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                {conversation.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-foreground-600">請直接寫下正在發生的真實事件。</p>
                    <p className="text-xs text-foreground-400 mt-2">AI Angel會辨識RG主章，以五秒歸零協助你重看全局、尋找主軸與下一步。</p>
                  </div>
                ) : (
                  conversation.map((entry, i) => (
                    <div
                      key={i}
                      className={`flex ${entry.from === 'angel' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[85%] rounded-xl px-4 py-3 ${
                          entry.from === 'angel'
                            ? 'bg-background-50 border border-foreground-200/30'
                            : 'bg-accent-500 text-background-50'
                        }`}
                      >
                        {entry.from === 'angel' && (
                          <div className="flex items-center gap-1.5 mb-1">
                            <AngelIcon variant="gold" size="sm" />
                            <span className="text-[10px] text-foreground-400">AI Angel</span>
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-line">{entry.text}</p>
                        {entry.textEn && (
                          <p className="text-[10px] opacity-60 mt-1">{entry.textEn}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {!submitted ? (
                <div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleConnectDotsReply(); }}
                      className="flex-1 px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-accent-300"
                      placeholder="例如：同事一句話讓我立刻想反擊……"
                    />
                    <button
                      onClick={handleConnectDotsReply}
                      disabled={!userInput.trim()}
                      className="px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      連結主章
                    </button>
                  </div>
                  {connectedChapters.length > 0 && (
                    <button
                      onClick={() => setModeCallback('treasureMap')}
                      className="mt-3 w-full text-xs text-secondary-600 hover:text-secondary-700 transition-colors cursor-pointer"
                    >
                      帶著 {connectedChapters.join(' → ')} 的主軸，前往 Treasure Map →
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-xs text-foreground-400 italic mb-3">
                    AI Angel 不替你連結。它等待你自己看見。
                  </p>
                  <button
                    onClick={() => { setConversation([]); setSubmitted(false); setUserInput(''); }}
                    className="text-xs text-accent-600 hover:text-accent-700 transition-colors cursor-pointer"
                  >
                    重新開始
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Treasure Map — level selection + conversation */}
          {mode === 'treasureMap' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-map-line text-secondary-500 text-lg" />
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('aiAngel.modes.treasureMap.name')}
                </h3>
              </div>
              <p className="text-xs text-foreground-400 mb-4">
                {t('aiAngel.modes.treasureMap.desc')}
              </p>

              {(lookFindNotes.length > 0 || connectedChapters.length > 0) && (
                <div className="rounded-xl border border-secondary-200 bg-secondary-50/50 px-4 py-4 mb-4">
                  <p className="text-[10px] tracking-wider text-secondary-600">前段歷程交接｜AI ANGEL 導航建議</p>
                  {lookFindNotes.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-foreground-600">Look & Find留下的看見</p>
                      <ul className="mt-1 space-y-1 text-xs text-foreground-500">
                        {lookFindNotes.map((note, index) => (
                          <li key={`${note}-${index}`}>「{note}」</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {connectedChapters.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-foreground-600">Connect Dots辨識的RG路徑</p>
                      <p className="text-xs text-foreground-500 mt-1">{connectedChapters.join(' → ')}</p>
                      {connectDotsResult && (
                        <details className="mt-2 text-xs text-foreground-500">
                          <summary className="cursor-pointer text-accent-600">查看章節判讀內容</summary>
                          <p className="mt-2 whitespace-pre-line leading-relaxed">{connectDotsResult}</p>
                        </details>
                      )}
                    </div>
                  )}
                  <div className="mt-3 border-t border-secondary-200/60 pt-3">
                    <p className="text-sm text-foreground-700">
                      因此，AI Angel建議目前先進入「{treasureStageLabel[recommendedTreasure]}」。
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">
                      {treasureStageRelation[recommendedTreasure]} 這是導航建議，不是強制判定；你的選擇會另外保存。
                    </p>
                  </div>
                </div>
              )}

              {/* Four-stage map remains visible and reviewable throughout the journey */}
              <div className="space-y-2 mb-4">
                  <p className="text-xs text-foreground-500 mb-2">今天，生命比較像是在邀請你……</p>
                  {[
                    { key: 'observe', color: 'border-foreground-200 hover:border-foreground-300', bg: 'hover:bg-foreground-50', text: 'text-foreground-600', icon: 'ri-eye-line' },
                    { key: 'reflect', color: 'border-primary-200 hover:border-primary-300', bg: 'hover:bg-primary-50', text: 'text-primary-700', icon: 'ri-pause-circle-line' },
                    { key: 'practice', color: 'border-accent-200 hover:border-accent-300', bg: 'hover:bg-accent-50', text: 'text-accent-700', icon: 'ri-riding-line' },
                    { key: 'reveal', color: 'border-secondary-200 hover:border-secondary-300', bg: 'hover:bg-secondary-50', text: 'text-secondary-700', icon: 'ri-sparkling-line' },
                  ].map((lvl) => (
                    <button
                      key={lvl.key}
                      onClick={() => {
                        handleTreasureLevel(
                          lvl.key as TreasureStage,
                          t(`aiAngel.modes.treasureMap.levels.${lvl.key}` as any),
                          t(`aiAngel.modes.treasureMap.levels.${lvl.key}En` as any),
                        );
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border ${lvl.color} ${lvl.bg} ${selectedTreasure === lvl.key ? 'ring-2 ring-secondary-300' : ''} transition-all cursor-pointer text-left`}
                    >
                      <i className={`${lvl.icon} ${lvl.text} text-lg`} />
                      <div className="flex-1">
                        <div className="flex items-center justify-between gap-2">
                        <p className={`text-sm font-medium ${lvl.text}`}>
                          {t(`aiAngel.modes.treasureMap.levels.${lvl.key}` as any)}
                        </p>
                        <span className="flex items-center gap-1">
                          {recommendedTreasure === lvl.key && (
                            <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[9px] text-secondary-700">AI建議</span>
                          )}
                          {selectedTreasure === lvl.key && (
                            <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[9px] text-primary-700">你已選擇</span>
                          )}
                        </span>
                        </div>
                        <p className="text-[10px] text-foreground-400">
                          {t(`aiAngel.modes.treasureMap.levels.${lvl.key}En` as any)}
                        </p>
                        <p className="text-[11px] text-foreground-500 mt-1">
                          {treasureStageRelation[lvl.key as TreasureStage]}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>

              {Object.keys(treasureNotes).length > 0 && (
                <div className="rounded-xl border border-foreground-200/50 bg-background-50 p-4 mb-4">
                  <p className="text-[10px] tracking-wider text-foreground-500">四階段歷程｜可回看、可補寫</p>
                  <div className="mt-3 space-y-2">
                    {(['observe', 'reflect', 'practice', 'reveal'] as TreasureStage[]).map((stage) => (
                      <div key={stage} className="grid grid-cols-[52px_1fr] gap-2 text-xs">
                        <span className="font-medium text-foreground-600">{treasureStageLabel[stage]}</span>
                        <span className={treasureNotes[stage] ? 'text-foreground-700' : 'text-foreground-300'}>
                          {treasureNotes[stage] || '尚未填寫'}
                        </span>
                      </div>
                    ))}
                  </div>
                  {treasureNotes.reveal && (
                    <div className="mt-3 border-t border-foreground-200/50 pt-3">
                      <p className="text-xs font-medium text-secondary-700">本次揭示</p>
                      <p className="text-xs leading-relaxed text-foreground-600 mt-1">
                        你從「{treasureNotes.observe || '尚待觀察'}」開始，看見「{treasureNotes.reflect || '尚待反思'}」，
                        決定實踐「{treasureNotes.practice || '尚待練習'}」，並提領出「{treasureNotes.reveal}」。
                      </p>
                    </div>
                  )}
                </div>
              )}

              {Object.keys(treasureNotes).length > 0 && (
                <div className="rounded-xl border border-amber-200/70 bg-amber-50/40 p-4 mb-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-foreground-700">Mentor &gt;5D RG 過關檢核</p>
                      <p className="text-xs text-foreground-500 mt-1">
                        尋寶圖不是只得到感受，而是讓中軸行動形成結果。此處是送 Mentor 覆核的準備條件，不由 AI 自行宣告維度過關。
                      </p>
                    </div>
                    <span className="rounded-full bg-amber-100 px-2 py-1 text-[10px] text-amber-700">
                      {Object.values(mentorCheckpoints).filter(Boolean).length}/6
                    </span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {(Object.keys(mentorCheckpointLabel) as MentorCheckpointKey[]).map((key) => (
                      <label key={key} className="flex items-start gap-2 text-xs text-foreground-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={Boolean(mentorCheckpoints[key])}
                          onChange={(event) => setMentorCheckpoints((items) => ({ ...items, [key]: event.target.checked }))}
                          className="mt-0.5"
                        />
                        <span>{mentorCheckpointLabel[key]}</span>
                      </label>
                    ))}
                  </div>
                  <details className="mt-3 border-t border-amber-200/60 pt-3 text-xs text-foreground-600">
                    <summary className="cursor-pointer font-medium text-amber-700">BMSA 中「隱導」如何層層升級？</summary>
                    <div className="mt-2 space-y-1.5 leading-relaxed">
                      <p><strong>Body｜身體：</strong>生命力能否承載？是在增長，還是在耗損？</p>
                      <p><strong>Mind｜心智：</strong>分清事實、推論與投射；開始突破小私：自我 → 家人 → 團體 → 家邦 → ≥5D生命共同體。</p>
                      <p><strong>Spirit｜高意念／意識：</strong>方向是否更接近本心、衷愛與大生命？守在中軸 → ≥5D錨定一軸。</p>
                      <p><strong>Action｜行：</strong>形成責任、期限、交付、修補與Evidence；小i → 服事 → 執事。</p>
                      <p className="pt-1 text-secondary-700">感覺到訊息 → 能辨識訊息 → 能正確使用 → ≥5D持續成為供應管道 → 最終7–8–9D成就生命。</p>
                    </div>
                  </details>
                  {Object.values(mentorCheckpoints).filter(Boolean).length === 6 && (
                    <p className="text-xs text-secondary-700 mt-3 border-t border-amber-200/60 pt-3">
                      六項已具備紀錄：可送 Mentor 依真實 Evidence、持續性與內外一致進行覆核。
                    </p>
                  )}
                </div>
              )}

              {(lookFindNotes.length > 0 || connectedChapters.length > 0 || Object.keys(treasureNotes).length > 0) && (
                <div className="rounded-xl border border-primary-200/60 bg-primary-50/40 p-4 mb-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-foreground-700">保存這段生命歷程</p>
                      <p className="text-xs text-foreground-500 mt-1">
                        保存 Look & Find、三章路徑、四階段內容與目前進度。
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={saveTreasureJourney}
                        className="px-4 py-2 rounded-md bg-primary-600 text-background-50 text-xs hover:bg-primary-700 transition-colors cursor-pointer"
                      >
                        {activeTreasureId ? '更新尋寶圖存檔' : '尋寶圖存檔'}
                      </button>
                      <button
                        onClick={() => setModeCallback('treasureArchive')}
                        className="px-4 py-2 rounded-md border border-primary-200 text-primary-700 text-xs hover:bg-primary-50 transition-colors cursor-pointer"
                      >
                        查看我的尋寶圖
                      </button>
                    </div>
                  </div>
                  {saveNotice && <p className="text-xs text-primary-700 mt-3">{saveNotice}</p>}
                </div>
              )}

              {/* Conversation after level selected */}
              {conversation.length > 0 && (
                <>
                  <div className="space-y-3 mb-4 max-h-[300px] overflow-y-auto">
                    {conversation.map((entry, i) => (
                      <div
                        key={i}
                        className={`flex ${entry.from === 'angel' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-xl px-4 py-3 ${
                            entry.from === 'angel'
                              ? 'bg-background-50 border border-foreground-200/30'
                              : 'bg-secondary-500 text-background-50'
                          }`}
                        >
                          {entry.from === 'angel' && (
                            <div className="flex items-center gap-1.5 mb-1">
                              <AngelIcon variant="gold" size="sm" />
                              <span className="text-[10px] text-foreground-400">AI Angel</span>
                            </div>
                          )}
                          <p className="text-sm whitespace-pre-line">{entry.text}</p>
                          {entry.textEn && (
                            <p className="text-[10px] opacity-60 mt-1">{entry.textEn}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>

                  {!submitted ? (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleTreasureMapReply(); }}
                        className="flex-1 px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-300 focus:outline-none focus:ring-1 focus:ring-secondary-300"
                        placeholder={selectedTreasure ? `寫下你的「${treasureStageLabel[selectedTreasure]}」……` : '請先選擇一個階段'}
                      />
                      <button
                        onClick={handleTreasureMapReply}
                        disabled={!userInput.trim()}
                        className="px-4 py-2 rounded-md bg-secondary-500 text-background-50 text-sm hover:bg-secondary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                      >
                        回應
                      </button>
                    </div>
                  ) : (
                    <div className="text-center">
                      <p className="text-xs text-foreground-400 italic mb-3 leading-relaxed">
                        AI Angel 永遠不給答案。它只照亮方向，讓你自己找到寶藏。
                      </p>
                      <button
                        onClick={() => { setConversation([]); setSubmitted(false); setUserInput(''); }}
                        className="text-xs text-secondary-600 hover:text-secondary-700 transition-colors cursor-pointer"
                      >
                        重新選擇階段
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* My Treasure Maps */}
          {mode === 'treasureArchive' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <div>
                  <div className="flex items-center gap-2">
                    <i className="ri-archive-line text-primary-600 text-lg" />
                    <h3 className="text-sm font-medium text-foreground-800">我的尋寶圖</h3>
                  </div>
                  <p className="text-xs text-foreground-500 mt-1">
                    叫回過去的看見、RG三章路徑與四階段歷程，從上次停下的位置繼續。
                  </p>
                </div>
                <button
                  onClick={startNewTreasureJourney}
                  className="px-4 py-2 rounded-md bg-primary-600 text-background-50 text-xs hover:bg-primary-700 transition-colors cursor-pointer"
                >
                  建立新尋寶圖
                </button>
              </div>

              {treasureArchives.length === 0 ? (
                <div className="rounded-xl border border-dashed border-foreground-200 bg-background-50 px-5 py-8 text-center">
                  <i className="ri-map-2-line text-2xl text-foreground-300" />
                  <p className="text-sm text-foreground-600 mt-2">目前還沒有尋寶圖存檔</p>
                  <p className="text-xs text-foreground-400 mt-1">從 Look & Find 開始，走到 Treasure Map 後即可保存。</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {treasureArchives.map((archive) => {
                    const stages: TreasureStage[] = ['observe', 'reflect', 'practice', 'reveal'];
                    const completedCount = stages.filter((stage) => Boolean(archive.treasureNotes?.[stage])).length;
                    return (
                      <div key={archive.id} className="rounded-xl border border-foreground-200/50 bg-background-50 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-medium text-foreground-800 truncate">{archive.title}</p>
                              <span className={`rounded-full px-2 py-0.5 text-[9px] ${archive.status === 'completed' ? 'bg-secondary-100 text-secondary-700' : 'bg-primary-100 text-primary-700'}`}>
                                {archive.status === 'completed' ? '四階段完成' : `進行中 ${completedCount}/4`}
                              </span>
                            </div>
                            <p className="text-[10px] text-foreground-400 mt-1">
                              最後更新：{new Date(archive.updatedAt).toLocaleString('zh-TW')}
                            </p>
                            {archive.connectedChapters?.length > 0 && (
                              <p className="text-xs text-foreground-600 mt-2">
                                RG路徑：{archive.connectedChapters.join(' → ')}
                              </p>
                            )}
                            {archive.lookFindNotes?.[0] && (
                              <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                                最初的看見：「{archive.lookFindNotes[0]}」
                              </p>
                            )}
                            <p className="text-[10px] text-amber-700 mt-2">
                              Mentor過關準備：{Object.values(archive.mentorCheckpoints || {}).filter(Boolean).length}/6
                            </p>
                          </div>
                          <button
                            onClick={() => restoreTreasureJourney(archive)}
                            className="px-3 py-2 rounded-md border border-secondary-200 text-secondary-700 text-xs hover:bg-secondary-50 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            叫回並繼續
                          </button>
                        </div>
                        <div className="grid grid-cols-4 gap-2 mt-3">
                          {stages.map((stage) => (
                            <div
                              key={stage}
                              className={`rounded-md px-2 py-1.5 text-center text-[10px] ${archive.treasureNotes?.[stage] ? 'bg-secondary-50 text-secondary-700' : 'bg-foreground-50 text-foreground-300'}`}
                            >
                              {treasureStageLabel[stage]}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <p className="text-[10px] text-foreground-400 mt-4">
                Alpha／Beta 預覽版先保存在目前瀏覽器；正式會員版再接帳號雲端同步。
              </p>
            </div>
          )}

          {/* Living Witness — NEW */}
          {mode === 'livingWitness' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-eye-2-line text-amber-400 text-lg" />
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('livingWitness.title')}
                </h3>
              </div>
              <p className="text-xs text-foreground-400 uppercase tracking-wider mb-1">
                {t('livingWitness.titleEn')}
              </p>
              <p className="text-xs text-foreground-500 leading-relaxed mb-1">
                {t('livingWitness.subtitle')}
              </p>
              <p className="text-[10px] text-foreground-400 leading-relaxed mb-5">
                {t('livingWitness.subtitleEn')}
              </p>

              {!witnessSent ? (
                <>
                  {/* Treasures that can be shared */}
                  <p className="text-xs text-foreground-600 mb-3 font-medium">
                    {t('livingWitness.invitation')}
                  </p>
                  <p className="text-[10px] text-foreground-400 mb-4">
                    {t('livingWitness.invitationEn')}
                  </p>

                  {/* Sample treasures */}
                  <div className="space-y-2 mb-5">
                    {[
                      { date: '07.21', treasure: '誠信', excerpt: '今天在工作中選擇了坦誠，即使這讓對話變得困難……' },
                      { date: '07.17', treasure: '覺知', excerpt: '第一次在感到煩躁時，沒有立刻反應，而是停了三秒……' },
                    ].map((t, i) => (
                      <div key={i} className="bg-background-50 border border-foreground-200/30 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] text-foreground-400">{t.date}</span>
                          <span className="text-xs font-medium text-foreground-700">{t.treasure}</span>
                        </div>
                        <p className="text-xs text-foreground-500 italic">{t.excerpt}</p>
                      </div>
                    ))}
                  </div>

                  {/* Visibility selection */}
                  <p className="text-xs text-foreground-400 mb-2">分享方式：</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {([
                      { key: 'anonymous' as const, icon: 'ri-user-unfollow-line', label: t('livingWitness.anonymous'), labelEn: t('livingWitness.anonymousEn') },
                      { key: 'public' as const, icon: 'ri-earth-line', label: t('livingWitness.public'), labelEn: t('livingWitness.publicEn') },
                      { key: 'mentorOnly' as const, icon: 'ri-user-heart-line', label: t('livingWitness.mentorOnly'), labelEn: t('livingWitness.mentorOnlyEn') },
                    ]).map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setWitnessVisibility(witnessVisibility === opt.key ? null : opt.key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-all cursor-pointer whitespace-nowrap ${
                          witnessVisibility === opt.key
                            ? 'border-amber-300 bg-amber-50 text-amber-700'
                            : 'border-foreground-200 text-foreground-500 hover:border-foreground-300'
                        }`}
                      >
                        <i className={`${opt.icon} text-sm`} />
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={handleShareWitness}
                      disabled={!witnessVisibility}
                      className="px-5 py-2 rounded-md bg-amber-400 text-white text-sm hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('livingWitness.share')}
                    </button>
                    <Link
                      to="/evidence"
                      className="px-5 py-2 rounded-md border border-foreground-200 text-foreground-500 text-sm hover:bg-foreground-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {t('livingWitness.later')}
                    </Link>
                  </div>
                </>
              ) : (
                /* Submitted state */
                <div className="text-center">
                  <i className="ri-check-double-line text-amber-400 text-2xl mb-3 inline-block" />
                  <p className="text-sm font-medium text-foreground-700 mb-1">
                    {t('livingWitness.submitted')}
                  </p>
                  <p className="text-xs text-foreground-400 uppercase tracking-wider mb-3">
                    {t('livingWitness.submittedEn')}
                  </p>
                  <p className="text-xs text-foreground-500 leading-relaxed max-w-md mx-auto">
                    {t('livingWitness.submittedDetail')}
                  </p>
                  <p className="text-[10px] text-foreground-400 max-w-md mx-auto mt-1">
                    {t('livingWitness.submittedDetailEn')}
                  </p>
                  <button
                    onClick={() => { setWitnessVisibility(null); setWitnessSent(false); }}
                    className="mt-4 text-xs text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                  >
                    分享另一份見證
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Angel Memory */}
          {mode === 'memory' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-history-line text-primary-500 text-lg" />
                <h3 className="text-sm font-medium text-foreground-800">
                  {t('journey.angelMemory.title')}
                </h3>
              </div>
              <p className="text-xs text-foreground-400 italic mb-5">
                {t('journey.angelMemory.subtitle')}
              </p>
              <p className="text-[10px] text-foreground-400 mb-4">
                {t('journey.angelMemory.subtitleEn')}
              </p>

              <div className="space-y-3">
                {[
                  { period: '今天', periodEn: 'Today', treasure: '誠信', note: '今天在工作中選擇了坦誠。' },
                  { period: '昨天', periodEn: 'Yesterday', treasure: '感恩', note: '早晨的陽光讓我停下來感謝。' },
                  { period: '三個月前', periodEn: '3 months ago', treasure: '誠信', note: '三個月前，誠信也是你的寶藏。今天，生命溫柔地邀請你重新看見它。' },
                ].map((mem, i) => (
                  <div key={i} className="bg-background-50 border border-foreground-200/30 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] text-foreground-400">{mem.period} · {mem.periodEn}</span>
                      <span className="text-xs font-medium text-primary-600">{mem.treasure}</span>
                    </div>
                    <p className="text-sm text-foreground-700">{mem.note}</p>
                  </div>
                ))}
              </div>

              <Link
                to="/evidence"
                className="inline-flex items-center gap-1 mt-5 text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-line" />
                查看完整寶藏時間軸
              </Link>
            </div>
          )}

          {/* Gratitude */}
          {mode === 'gratitude' && (
            <div className="bg-background-100 border border-foreground-200/40 rounded-xl p-6 text-center">
              <i className="ri-heart-3-fill text-rose-300 text-2xl mb-3 inline-block" />
              <h3 className="text-sm font-medium text-foreground-800 mb-2">
                {t('aiAngel.modes.gratitude.name')}
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-2">
                {t('aiAngel.modes.gratitude.desc')}
              </p>
              <p className="text-xs text-foreground-400 italic mb-5">
                {t('aiAngel.modes.gratitude.descEn')}
              </p>
              <p className="text-xs text-foreground-400">
                請點擊右下角的 AI Angel + 感恩回應圖標，隨時寫下你的感恩。
              </p>
            </div>
          )}
        </div>

        {/* ── Language Note ─────────────────────────── */}
        <div className="max-w-2xl mx-auto mt-10 bg-background-100 border border-foreground-200/30 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <i className="ri-information-line text-foreground-400 text-sm mt-0.5" />
            <div>
              <p className="text-xs text-foreground-500 mb-1">
                {t('aiAngel.language.neverUse')}
              </p>
              <p className="text-[11px] text-foreground-400 line-through mb-2">
                {t('aiAngel.language.forbiddenList')}
              </p>
              <p className="text-xs text-foreground-500 mb-1">
                {t('aiAngel.language.alwaysUse')}
              </p>
              <p className="text-[11px] text-primary-600">
                {t('aiAngel.language.allowedList')}
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
